/**
 * 《传灯》Web 原型入口
 *
 * 职责边界：这一层只做**编排**（拉数据、切屏、串流程），
 * 游戏规则在 `@chuandeng/shared`，对局状态在 `game/session.ts`，
 * 渲染在 `render/` 与 `ui/`。这样迁 Cocos 时替换的是渲染层与本文件的屏幕切换。
 *
 * 屏幕：章节选择 → 对局（地图 + HUD）→ 结算。
 */

import {
  CHAPTERS,
  resolveConstants,
  type ChapterConfig,
  type LevelConfig,
  type SimEvent,
} from "@chuandeng/shared";
import "./styles.css";
import {
  GameApi,
  cacheProgress,
  readCachedProgress,
  type SessionUser,
} from "./api/client.js";
import { ALL_CARDS, cardById, collectionStats, quizPoolForLevel } from "./content/cards.js";
import { QuizScheduler, type QuizPrompt } from "./game/quiz.js";
import { createSession, type GameSession } from "./game/session.js";
import { MapView } from "./render/map.js";
import { createHud, type Hud } from "./render/hud.js";
import { BrowserStore, MemoryStore, type KeyValueStore } from "./store/storage.js";
import { LocalProgressStore } from "./store/localProgress.js";
import { WrongAnswerStore } from "./store/wrongAnswers.js";
import { openCodex } from "./ui/codex.js";
import { esc, mustFind, starsText } from "./ui/dom.js";
import { askQuiz } from "./ui/quiz.js";
import { closeAllModals, infoModal } from "./ui/modal.js";
import { openSettlement } from "./ui/settlement.js";
import { openSkillTree } from "./ui/skillTree.js";
import { toast } from "./ui/toast.js";
import { mountStats, type StatsOverlay } from "./dev/stats.js";

// ---------- 应用状态 ----------

interface RestoredRun {
  mapSeed: number;
  eventSeed: number;
  actions: ReturnType<GameSession["snapshot"]>["actions"];
}

interface AppState {
  store: KeyValueStore;
  api: GameApi;
  user: SessionUser | null;
  chapters: ChapterConfig[];
  progress: LocalProgressStore;
  wrongAnswers: WrongAnswerStore;
  session: GameSession | null;
  mapView: MapView | null;
  hud: Hud | null;
  scheduler: QuizScheduler | null;
  quizQueue: SimEvent[];
  quizPending: boolean;
  selectedRegionId: string | null;
  savedAtActionCount: number;
  stats: StatsOverlay | null;
}

const DEBUG = import.meta.env.DEV;
const SAVE_DEBOUNCE_ACTIONS = 3;
/**
 * API 基地址。默认空字符串 = 同源，这是两种正式形态下都成立的选择：
 * 本地开发由 Vite 代理 /v1 到 Worker，线上由 Pages Function 把 /v1 同源转发到 Worker。
 * 只有把静态包托管到"没有代理能力"的地方时，才需要设 VITE_API_BASE 指向 API 域名。
 */
const API_BASE = String(import.meta.env["VITE_API_BASE"] ?? "").trim();

function createStore(): KeyValueStore {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return new MemoryStore();
  }
  return new BrowserStore();
}

/** 只有一份底层存储实例：三处各建一个会导致读写的不是同一处 */
const store = createStore();

const state: AppState = {
  store,
  api: new GameApi({ store, ...(API_BASE ? { baseUrl: API_BASE } : {}) }),
  user: null,
  chapters: [],
  progress: new LocalProgressStore(store),
  wrongAnswers: new WrongAnswerStore(store),
  session: null,
  mapView: null,
  hud: null,
  scheduler: null,
  quizQueue: [],
  quizPending: false,
  selectedRegionId: null,
  savedAtActionCount: 0,
  stats: null,
};

// ---------- 启动 ----------

async function boot(): Promise<void> {
  const session = await state.api.ensureSession();
  if (session.ok) {
    state.user = session.data;
    setUserChip(session.data.displayName);
  } else {
    setUserChip("离线游客");
    if (!session.offline) toast("账号服务暂时不可用，先本地玩也可以", "warn");
  }

  await loadChapters();
  await refreshProgress();
  void syncWrongAnswers();

  // 支持 #level=c1-l1 深链：刷新后回到同一关，也方便把某一关的链接发给别人
  const deepLink = levelIdFromHash();
  if (deepLink) await startLevel(deepLink);
  else renderChapterSelect();
}

function levelIdFromHash(): string | null {
  const match = /^#level=(.+)$/.exec(window.location.hash);
  if (!match?.[1]) return null;
  const id = decodeURIComponent(match[1]);
  return findLevel(id) ? id : null;
}

function setLevelHash(levelId: string | null): void {
  const next = levelId ? `#level=${encodeURIComponent(levelId)}` : "";
  const current = window.location.hash;
  if (current === next) return;
  // 保留 query（?stats=1 这类调试参数不该因为进入关卡而消失）
  const { pathname, search } = window.location;
  // replaceState：不往浏览器历史里塞一堆中间状态，返回键仍然是"回上一页"
  window.history.replaceState(null, "", `${pathname}${search}${next}`);
}

async function loadChapters(): Promise<void> {
  const remote = await state.api.listChapters();
  if (remote.ok) {
    // 服务端返回的是章节摘要；关卡细节用打进包体的注册表（同一份 shared 数据）
    const known = new Set(remote.data.chapters.map((summary) => summary.id));
    const matched = CHAPTERS.filter((chapter) => known.has(chapter.id));
    state.chapters = matched.length > 0 ? matched : [...CHAPTERS];
    return;
  }

  // 离线降级：直接用内置关卡数据，孩子不至于因为断网就打不开游戏
  state.chapters = [...CHAPTERS];
  if (!remote.offline) toast("关卡数据加载失败，使用内置版本", "warn");
}

async function refreshProgress(): Promise<void> {
  const remote = await state.api.fetchProgress();
  if (remote.ok) {
    cacheProgress(state.store, remote.data);
    if (state.user) setUserChip(`${state.user.displayName} · 灯油 ${remote.data.lampOil}`);
    return;
  }
  const cached = readCachedProgress(state.store);
  if (cached && !remote.offline) cacheProgress(state.store, cached);
}

async function syncWrongAnswers(): Promise<void> {
  const pending = state.wrongAnswers.pendingSyncCardIds();
  if (pending.length === 0) return;
  const result = await state.api.syncWrongAnswers(pending);
  if (result.ok) state.wrongAnswers.markSynced(pending);
}

function setUserChip(text: string): void {
  const chip = document.getElementById("user-chip");
  if (chip) chip.textContent = text;
}

// ---------- 章节选择 ----------

function mergedProgress(): ReturnType<LocalProgressStore["list"]> {
  const cached = readCachedProgress(state.store);
  return LocalProgressStore.merge(cached?.levels ?? [], state.progress.list());
}

function renderChapterSelect(): void {
  setLevelHash(null);
  state.stats?.destroy();
  state.stats = null;
  state.session = null;
  state.mapView?.destroy();
  state.mapView = null;
  state.hud = null;
  closeAllModals();

  const screen = mustFind<HTMLElement>("#screen");
  const chapter = state.chapters[0];
  const rows = mergedProgress();
  const stats = collectionStats(readCachedProgress(state.store)?.ownedCardIds ?? []);
  const wrongStats = state.wrongAnswers.stats();
  const hasChapterData = CHAPTERS.length > 0;

  screen.className = "screen-chapter";
  screen.innerHTML = `
    <section class="hero paper">
      <h1 class="hero-title">${esc(chapter?.title ?? "传灯")}</h1>
      <p class="hero-sub">${esc(chapter?.description ?? "用真实的知识点燃火种，驱散遗忘的迷雾。")}</p>
      <div class="hero-stats">
        <span class="stat-pill">图鉴 <strong>${stats.owned}</strong> / ${stats.total}</span>
        <span class="stat-pill">已审核卡 <strong>${stats.publishable}</strong> / ${stats.total}</span>
        <span class="stat-pill">错题待复习 <strong>${wrongStats.due}</strong></span>
        <span class="stat-pill">待复习合计 <strong>${wrongStats.pending}</strong></span>
      </div>
      ${
        state.api.offline
          ? `<p class="hero-sub" style="margin-top:.6rem">当前处于离线状态：可以正常游玩，成绩会先在本地记账。</p>`
          : ""
      }
    </section>
    <div class="level-list">
      ${(chapter?.levels ?? [])
        .map((level) => {
          const row = rows.find((r) => r.levelId === level.id);
          return `
            <button class="level-card paper" type="button" data-level="${esc(level.id)}">
              <div class="level-card-head">
                <span class="level-index">${level.index}</span>
                <span class="level-title">${esc(level.title)}</span>
                ${starsText(row?.stars ?? 0)}
              </div>
              <p class="level-teach">${esc(level.teach)}</p>
              <div class="level-meta">
                <span>目标启蒙度 ${level.goalEnlightenment}</span>
                <span>回合 ${level.turnLimit}</span>
                <span>迷雾 ${level.mistGrowth}/回合</span>
                <span>${level.regionIds.length} 个区域</span>
                ${row ? `<span>最好成绩 ${row.bestScore}</span>` : ""}
              </div>
            </button>`;
        })
        .join("")}
    </div>
    ${
      hasChapterData
        ? ""
        : `<p style="color:var(--ink-on-dark)">没有可用的关卡数据，请检查内容包。</p>`
    }`;

  screen.onclick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLElement>("[data-level]");
    const levelId = button?.dataset["level"];
    if (levelId) void startLevel(levelId);
  };
}

// ---------- 对局 ----------

function randomSeed(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0]!;
}

async function startLevel(levelId: string): Promise<void> {
  const level = findLevel(levelId);
  if (!level) {
    toast("找不到这一关", "error");
    return;
  }

  let restored: RestoredRun | null = null;
  const run = await state.api.loadRun(levelId);
  if (run.ok && run.data.actions.length > 0) {
    restored = {
      mapSeed: run.data.mapSeed,
      eventSeed: run.data.eventSeed,
      actions: run.data.actions,
    };
  }

  const session = createSession({
    level,
    mapSeed: restored?.mapSeed ?? randomSeed(),
    eventSeed: restored?.eventSeed ?? randomSeed(),
    ...(restored ? { actions: restored.actions } : {}),
  });
  state.session = session;
  setLevelHash(level.id);
  state.selectedRegionId = null;
  state.quizQueue = [];
  state.quizPending = false;
  state.savedAtActionCount = session.actions.length;

  const pool = quizPoolForLevel(level);
  state.scheduler = new QuizScheduler(pool.cards, { includesDraft: pool.includesDraft });
  if (pool.cards.length === 0) {
    toast("这一关暂时还没有可用的题目", "warn");
  } else if (pool.includesDraft && DEBUG) {
    toast(`题池含 ${pool.cards.length} 张待审核卡片（开发模式）`, "warn");
  }

  buildPlayScreen(restored !== null);
}

function findLevel(levelId: string): LevelConfig | undefined {
  for (const chapter of state.chapters) {
    const found = chapter.levels.find((level) => level.id === levelId);
    if (found) return found;
  }
  for (const chapter of CHAPTERS) {
    const found = chapter.levels.find((level) => level.id === levelId);
    if (found) return found;
  }
  return undefined;
}

function buildPlayScreen(restored: boolean): void {
  const screen = mustFind<HTMLElement>("#screen");
  screen.className = "screen-play";
  screen.innerHTML = `
    <div class="play-main">
      <section class="hud paper"></section>
      <div class="map-frame">
        <canvas class="map-canvas" aria-label="世界地图"></canvas>
        <div class="map-hint">点击区域扎营 · 点击路线传播火种</div>
      </div>
      <section class="action-bar paper"></section>
    </div>
    <aside class="side"></aside>`;

  const hud = createHud({
    onSpread: (routeId) => doAction(() => state.session?.spread(routeId) ?? false),
    onIgnite: (regionId) => {
      doAction(() => state.session?.ignite(regionId) ?? false);
      state.selectedRegionId = regionId;
      refresh();
    },
    onOpenTree: () => openTree(),
    onEndTurn: () => endTurn(),
  });
  state.hud = hud;
  screen.querySelector(".hud")?.replaceWith(hud.hud);
  screen.querySelector(".action-bar")?.replaceWith(hud.actions);
  screen.querySelector(".side")?.replaceWith(hud.side);

  const canvas = mustFind<HTMLCanvasElement>(".map-canvas", screen);
  state.mapView = new MapView(canvas, {
    onRegionClick: (regionId) => {
      state.selectedRegionId = state.selectedRegionId === regionId ? null : regionId;
      refresh();
    },
    onRouteClick: (routeId) => doAction(() => state.session?.spread(routeId) ?? false),
    onRenderError: () => toast("地图渲染出了点问题，界面其余部分仍可操作", "warn", 5000),
  });

  // ?stats=1 时挂性能悬浮层（未带参数返回 null，正式玩家看不到）
  state.stats?.destroy();
  state.stats = mountStats(() => {
    const current = state.session;
    const size = state.mapView?.canvasSize ?? { width: 0, height: 0 };
    return {
      regions: current?.options.regionCount ?? 0,
      lit: current?.options.litCount ?? 0,
      mist: current?.state.mist ?? 0,
      canvasWidth: size.width,
      canvasHeight: size.height,
    };
  });

  if (restored) toast("已从云端恢复上次未完成的对局", "ok");
  refresh();
  state.session?.drainEvents();
}

function refresh(): void {
  const session = state.session;
  const hud = state.hud;
  const mapView = state.mapView;
  if (!session || !hud || !mapView) return;

  const options = session.options;
  const selection = (() => {
    const regionId = state.selectedRegionId;
    if (!regionId) {
      return { regionId: null, regionName: "", ignitable: false, currentEnlightenment: 0 };
    }
    const option = options.regions.find((r) => r.region.id === regionId);
    return {
      regionId,
      regionName: option?.region.name ?? regionId,
      ignitable: option?.ignitable ?? false,
      ...(option?.reason ? { reason: option.reason } : {}),
      currentEnlightenment: session.state.regions[regionId]?.enlightenment ?? 0,
    };
  })();

  hud.update({
    levelIndex: session.level.index,
    levelTitle: session.level.title,
    state: session.state,
    options,
    log: session.recentEvents(3),
    goal: { current: session.goal.current, target: session.goal.target },
    selection,
    quizPending: state.quizPending,
  });

  mapView.update(session.setup, session.state, options);
}

/** 执行一次操作：成功就刷新；结束了就走结算 */
function doAction(run: () => boolean): void {
  const session = state.session;
  if (!session) return;
  if (state.quizPending) {
    toast("先答完这道题吧", "warn");
    return;
  }
  if (!run()) {
    toast("这一步暂时做不到", "warn");
    return;
  }
  handleNewEvents();
  if (session.finished) {
    void finishLevel();
    return;
  }
  refresh();
  void autosaveRun();
}

function endTurn(): void {
  const session = state.session;
  if (!session) return;
  if (state.quizPending) {
    toast("先答完这道题吧", "warn");
    return;
  }
  if (!session.endTurn()) {
    toast("本回合没有可以推进的了", "warn");
    return;
  }
  handleNewEvents();
  if (session.finished) {
    void finishLevel();
    return;
  }
  refresh();
  void autosaveRun();
}

/** 把新事件里该出题的排进队列，然后逐题弹出 */
function handleNewEvents(): void {
  const session = state.session;
  const scheduler = state.scheduler;
  if (!session || !scheduler) return;
  for (const event of session.drainEvents()) {
    if (scheduler.shouldAsk(event)) state.quizQueue.push(event);
  }
  void runQuizQueue();
}

async function runQuizQueue(): Promise<void> {
  const session = state.session;
  const scheduler = state.scheduler;
  if (!session || !scheduler || state.quizPending) return;

  const event = state.quizQueue.shift();
  if (!event) return;

  const prompt = scheduler.pick(event, session.state.turn);
  if (!prompt) return;

  state.quizPending = true;
  refresh();

  showQuiz(prompt, session);
}

function showQuiz(prompt: QuizPrompt, session: GameSession): void {
  const scheduler = state.scheduler;
  const total = scheduler?.limit ?? 8;
  const index = (scheduler?.askedCount ?? 1) > 0 ? scheduler!.askedCount : 1;

  askQuiz(prompt, {
    progressLabel: `本局第 ${index} / ${total} 题`,
    onAnswered: (correct) => {
      // 把答题结果写回操作序列：这是"答对即收集知识卡"的唯一途径
      session.answer(`${prompt.card.id}-q`, correct, prompt.card.id);
      if (!correct) {
        state.wrongAnswers.add(prompt.card.id);
      } else {
        // 答对则清掉该卡的错题（间隔复习的"答对清零"）
        state.wrongAnswers.resolve(prompt.card.id, true);
        void state.api.resolveWrongAnswer(prompt.card.id, true);
      }
    },
    onClose: () => {
      state.quizPending = false;
      if (session.finished) {
        void finishLevel();
        return;
      }
      refresh();
      void runQuizQueue();
      void autosaveRun();
    },
  });
}

async function autosaveRun(): Promise<void> {
  const session = state.session;
  if (!session || session.finished) return;
  if (session.actions.length - state.savedAtActionCount < SAVE_DEBOUNCE_ACTIONS) return;
  state.savedAtActionCount = session.actions.length;
  const snapshot = session.snapshot();
  const result = await state.api.saveRun(
    snapshot.levelId,
    snapshot.mapSeed,
    snapshot.eventSeed,
    snapshot.actions,
  );
  if (!result.ok && !result.offline) toast("云端存档暂时失败，不影响继续玩", "warn");
}

// ---------- 结算 ----------

async function finishLevel(): Promise<void> {
  const session = state.session;
  if (!session) return;
  state.quizQueue = [];
  state.quizPending = false;

  const localStars = session.stars;
  const localScore = session.result.score;
  state.progress.record(session.level.chapterId, session.level.id, localScore, localStars);

  const snapshot = session.snapshot();
  const submitted = await state.api.submitProgress({
    levelId: snapshot.levelId,
    mapSeed: snapshot.mapSeed,
    eventSeed: snapshot.eventSeed,
    actions: snapshot.actions,
    clientChecksum: session.checksum,
  });

  // 服务端复算是权威；不可用时降级为本地预演算结果，并明确告知
  const outcome = submitted.ok
    ? {
        score: submitted.data.score,
        stars: submitted.data.stars,
        turns: submitted.data.turns,
        mist: submitted.data.mist,
        newCardIds: submitted.data.newCardIds,
        wrongCardIds: submitted.data.wrongCardIds,
        servedBy: "server" as const,
        lampOil: undefined as number | undefined,
        checksumMatched: submitted.data.checksumMatched,
      }
    : {
        score: localScore,
        stars: localStars,
        turns: session.state.turn,
        mist: session.state.mist,
        newCardIds: session.state.unlockedCardIds,
        // 本局答错的卡（与 shared 的规则一致：answer + correct=false + cardId）
        wrongCardIds: [
          ...new Set(
            session.actions
              .filter((action) => action.t === "answer" && !action.correct && action.cardId)
              .map((action) => (action as { cardId?: string }).cardId ?? ""),
          ),
        ].filter(Boolean),
        servedBy: "local" as const,
        lampOil: undefined,
        checksumMatched: undefined,
      };

  if (!submitted.ok && submitted.offline) {
    toast("离线状态：本局先在本地记账，联网后会自动上报", "warn");
  } else if (!submitted.ok) {
    toast(`成绩上传被拒绝（${submitted.error}），已按本地结果展示`, "warn");
  } else if (outcome.checksumMatched === false) {
    // 出现这种情况说明客户端与服务端的模拟核心产生了分歧，是最需要排查的信号
    console.error("checksum mismatch", {
      levelId: snapshot.levelId,
      actionCount: snapshot.actions.length,
    });
    toast("检测到本地与服务端结果不一致，已上传服务端版本", "warn");
  }

  // 本地进度也更新成权威结果
  state.progress.record(session.level.chapterId, session.level.id, outcome.score, outcome.stars);
  // 云端图鉴/灯油以 /v1/progress 为准（异步拉取，不阻塞结算展示）
  void refreshProgress();

  const view = {
    levelTitle: session.level.title,
    levelIndex: session.level.index,
    outcome: session.state.outcome === "win" ? ("win" as const) : ("lose" as const),
    stars: outcome.stars,
    score: outcome.score,
    turns: outcome.turns,
    turnLimit: resolveConstants(session.setup.constants).turnLimit,
    mist: outcome.mist,
    averageEnlightenment: session.options.averageEnlightenment,
    goalEnlightenment: session.setup.goalEnlightenment,
    litCount: session.options.litCount,
    regionCount: session.options.regionCount,
    newCards: outcome.newCardIds.map(cardById).filter((c): c is NonNullable<typeof c> => Boolean(c)),
    wrongCards: outcome.wrongCardIds
      .map(cardById)
      .filter((c): c is NonNullable<typeof c> => Boolean(c)),
    servedBy: outcome.servedBy,
    ...(outcome.lampOil === undefined ? {} : { lampOil: outcome.lampOil }),
  };

  openSettlement(view, {
    onReplay: () => void startLevel(session.level.id),
    onBack: () => renderChapterSelect(),
    onOpenCodex: (cardId) => {
      const card = cardById(cardId);
      if (card) openCodex(card, { origin: "结算 · 新收图鉴" });
    },
    onReviewWrong: (cardId) => void reviewCard(cardId),
  });
}

// ---------- 错题复习 ----------

async function reviewCard(cardId: string): Promise<void> {
  const card = cardById(cardId);
  if (!card) return;
  openCodex(card, {
    origin: "错题复习",
    interactiveQuiz: true,
    onAnswered: (correct) => {
      state.wrongAnswers.resolve(cardId, correct);
      void lastResolve(cardId, correct);
    },
  });
}

async function lastResolve(cardId: string, correct: boolean): Promise<void> {
  const result = await state.api.resolveWrongAnswer(cardId, correct);
  if (result.ok && correct) toast("这张卡已经过关，之后不用再复习啦", "ok");
}

// ---------- 技能树 ----------

function openTree(): void {
  const session = state.session;
  if (!session) return;
  openSkillTree({
    getView: () => ({
      setup: session.setup,
      state: session.state,
      options: session.options,
      level: session.level,
    }),
    onUpgrade: (nodeId) => {
      const upgraded = session.upgrade(nodeId);
      if (upgraded) {
        refresh();
        void autosaveRun();
      }
      return upgraded;
    },
    onOpenCodex: (cardId) => {
      const card = cardById(cardId);
      if (card) {
        openCodex(card, { origin: "初次解锁 · 知识图鉴", onRead: () => void onCardRead(cardId) });
      }
    },
  });
}

async function onCardRead(cardId: string): Promise<void> {
  const result = await state.api.markCardsRead([cardId]);
  if (!result.ok && !result.offline) toast("图鉴标记未能同步", "warn");
}

// ---------- 全局按钮 ----------

function wireGlobalChrome(): void {
  document.getElementById("btn-refresh")?.addEventListener("click", () => {
    void (async () => {
      await refreshProgress();
      await syncWrongAnswers();
      toast("存档已刷新", "ok");
      if (!state.session) renderChapterSelect();
    })();
  });
}

// ---------- 启动 ----------

wireGlobalChrome();
void boot().catch((error: unknown) => {
  console.error(error);
  infoModal(
    "出问题了",
    `<p>页面初始化失败。可以先刷新试试；如果一直不行，请把浏览器控制台的报错发给开发者。</p>
     <p style="color:var(--ink-soft);font-size:.82rem">${esc(String(error))}</p>`,
  );
});

if (DEBUG) {
  // 开发期方便在控制台里查看卡库与关卡
  Object.assign(window as unknown as Record<string, unknown>, { __chuandeng: { state, ALL_CARDS } });
}
